// src/components/ui/EditableWidget.jsx

import { useState } from 'react';
import { Pencil, Save, X, Loader2 } from 'lucide-react';
// Nota: No importamos 'ReactNode' porque es un 'tipo' de TypeScript

/**
 * Un componente de contenedor que permite alternar entre un widget de 
 * visualización y un widget de edición.
 */
export const EditableWidget = ({
  title,
  displayWidget,
  editWidget,
  onSave,
  isLoading = false,
  canEdit = true,
  className = '',
}) => {
  const [isEditing, setIsEditing] = useState(false);

  // --- Manejadores de Eventos ---

  const handleSaveClick = async () => {
    // Llama a la función onSave provista por el padre
    await onSave();
    // Sale del modo de edición *después* de que onSave se complete
    setIsEditing(false);
  };

  const handleCancelClick = () => {
    if (isLoading) return; // No permitir cancelar si está guardando
    setIsEditing(false);
  };

  // --- Renderizado del Header (Botones) ---
  const renderHeaderControls = () => {
    if (isEditing) {
      return (
        <div className="flex items-center gap-1">
          {/* Botón de Cancelar */}
          <button
            type="button"
            onClick={handleCancelClick}
            disabled={isLoading}
            className="p-2 rounded-full text-gray-500 hover:bg-gray-100 disabled:opacity-50 transition-colors"
            aria-label="Cancelar"
            title="Cancelar"
          >
            <X className="w-5 h-5" />
          </button>
          
          {/* Botón de Guardar */}
          <button
            type="button"
            onClick={handleSaveClick}
            disabled={isLoading}
            className="p-2 rounded-full text-blue-600 hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            aria-label="Guardar cambios"
            title="Guardar"
          >
            {isLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Save className="w-5 h-5" />
            )}
          </button>
        </div>
      );
    }

    if (canEdit) {
      return (
        /* Botón de Editar */
        <button
          type="button"
          onClick={() => setIsEditing(true)}
          className="p-2 rounded-full text-blue-600 hover:bg-blue-100 transition-colors"
          aria-label={`Editar ${title}`}
          title="Editar"
        >
          <Pencil className="w-5 h-5" />
        </button>
      );
    }

    return null; // No mostrar nada si no se puede editar y no se está editando
  };

  // --- Renderizado del Componente ---
  return (
    <div className={`border rounded-lg shadow-sm bg-white ${className}`}>
      {/* Cabecera con Título y Botones */}
      <div className="flex justify-between items-center p-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        <div className="flex items-center">
          {renderHeaderControls()}
        </div>
      </div>

      {/* Cuerpo (Contenido del Widget) */}
      <div className="p-4">
        {isEditing ? editWidget : displayWidget}
      </div>
    </div>
  );
};